import { useContext, useMemo } from "react";

import { DataContext } from "contexts/Data";
import { filterVariants, sortVariants, findVariant } from "helpers/variant";

import type { Variant, VariantOptions } from "types/app";
import { MODELS } from "shared/enum";

/**
 * Only use this hook under DataContext
 */
export default function useUpdateVariant(): (
  requirements: VariantOptions,
  customVariants?: Variant[],
  preferPriorityBasedSort?: boolean,
) => void {
  const { variants, cachedState, setCachedState } = useContext(DataContext);

  const variant = useMemo(() => {
    return findVariant(variants, cachedState.variantId);
  }, [variants, cachedState.variantId]) as Variant;

  return (
    requirements: VariantOptions,
    customVariants?: Variant[],
    preferPriorityBasedSort = false,
  ) => {
    const firstPassFilteredVariants = filterVariants(customVariants || variants, requirements);

    if (firstPassFilteredVariants.length === 1) {
      return setCachedState((prev) => ({
        ...prev,
        variantId: firstPassFilteredVariants[0].id,
        model: MODELS[firstPassFilteredVariants[0].code],
      }));
    }

    const secondPassVariants =
      firstPassFilteredVariants.length === 0 ? variants : firstPassFilteredVariants;

    const [bestMatch] = sortVariants(
      secondPassVariants,
      {
        ...variant,

        /* unset below unimportant factors */
        id: undefined,
        price: undefined,
        motorPower: undefined,
        motorTorque: undefined,

        ...requirements,
      },
      preferPriorityBasedSort,
    );

    setCachedState((prev) => ({ ...prev, variantId: bestMatch.id, model: MODELS[bestMatch.code] }));
  };
}
